import { useUserStore } from '@/store/modules/user/store.js';
import { isEmpty } from 'lodash-es';
import * as Sentry from '@sentry/vue';
import api from '@/api/index.ts';
import router from '@/router/index.ts';
import { showError, showWarning } from '@/utils/toastr.js';
import { empty, merge } from '@/utils/index.js';
import { useClientCardStore } from '@/store/modules/clientCard/store.js';
import { useDashboardStore } from '@/store/modules/dashboard/store.js';
import { useContextStore } from '@/store/modules/context/store.ts';
import axios from '@/plugins/axios.js';
import { assignmentTraceOptions } from '@/utils/assignmentTrace.ts';
import eventBus from '@/plugins/event-bus.ts';
import i18n from '@/plugins/vue-i18n.js';
import { notify } from '@/plugins/vue-notification.ts';
import { useAccountStore } from '@/store/modules/account/store.js';
import { assertEntityImport } from '@/utils/assertEntityImport.ts';
import { invalidateCustomerProfileLocally } from '@/components/customerProfile/invalidation/customerProfileInvalidation.ts';
import { useAssociatedLeadsStore } from './modules/associatedLeads/store.js';
import Moment from '../value-objects/Moment.js';
import CommunicationKind from '../entities/CommunicationKind.js';

import LeadType from '../entities/LeadType.js';

const applyLeadTypeDefaultsOnContextLead = (leadId, newData) => {
    const contextStore = useContextStore();
    if (contextStore.leadId != leadId || newData.lead_type_id === undefined) {
        return;
    }

    const lead = contextStore.lead;
    const presentedDate = Moment.asLocale(lead.presented_date, 'presented_date');
    const callDate = Moment.asLocale(lead.call_date, 'call_date');

    if (newData.lead_type_id == LeadType.WALK_IN && !presentedDate.isValid()) {
        newData.presented = true;
        newData.presented_date = Moment.now().toString();
    }

    if (newData.lead_type_id == LeadType.PHONE && !callDate.isValid()) {
        newData.call_date = Moment.now().toString();
    }

    if (
        newData.lead_type_id == LeadType.RENEWAL &&
        ['invalid', 'duplicate'].includes(lead.status)
    ) {
        newData.status = null;
    }
};

const invalidateLeadCustomerProfile = (customerId) => {
    invalidateCustomerProfileLocally({
        customerId,
        payloads: ['leads', 'activity'],
    });
};

const invalidateLeadVehicleCustomerProfile = (customerId) => {
    invalidateCustomerProfileLocally({
        customerId,
        payloads: ['leads'],
    });
};

const getKnownLeadCustomerId = (leadId) => {
    const contextLead = useContextStore().lead;
    if (contextLead?.id == leadId) {
        return contextLead.customer_id ?? contextLead.customer?.id;
    }

    const dashboardLead = useDashboardStore().tableLeads.find((lead) => lead.id == leadId);
    if (dashboardLead) {
        return dashboardLead.customer_id ?? dashboardLead.customer?.id;
    }

    const associatedLead = useAssociatedLeadsStore().associatedLeads.find(
        (lead) => lead.id == leadId
    );
    return associatedLead?.customer_id ?? associatedLead?.customer?.id;
};

export default {
    resetState() {
        this.$reset();
    },

    setPageTitle(pageTitle) {
        this.pageTitle = pageTitle;
    },

    setMergedLeadId(mergedLeadId) {
        this.mergedLeadId = mergedLeadId;
    },

    updateLead({ id, newData }) {
        const updateLead = (lead) => {
            if (
                newData.hasOwnProperty('wanted_vehicles') ||
                newData.hasOwnProperty('exchange_vehicles')
            ) {
                newData.vehicles = [
                    ...(newData.wanted_vehicles || lead.wanted_vehicles),
                    ...(newData.exchange_vehicles || lead.exchange_vehicles),
                ];
            }

            merge(lead, newData);
        };

        if (useContextStore().leadId == id) {
            updateLead(useContextStore().lead);
        }
        useDashboardStore()
            .tableLeads.filter((lead) => lead.id == id)
            .forEach(updateLead);
        useAssociatedLeadsStore()
            .associatedLeads.filter((lead) => lead.id == id)
            .forEach(updateLead);
    },

    updateLeadCustomable({ id, newData }) {
        const updateCustomable = (lead) => {
            lead.fillCustomable(newData.id, newData);
        };

        if (useContextStore().leadId == id) {
            updateCustomable(useContextStore().lead);
        }
        useDashboardStore()
            .tableLeads.filter((lead) => lead.id == id)
            .forEach(updateCustomable);
    },

    updateLeadProduct(leadProduct) {
        updateOrAddObjectInLead('products', leadProduct);
    },

    addLeadPhone(leadPhone) {
        updateOrAddObjectInLead('phones', leadPhone);
    },

    updateLeadPhone(leadPhone) {
        updateOrAddObjectInLead('phones', leadPhone);
    },

    deleteLeadPhone(leadPhone) {
        deleteObjectInLead('phones', leadPhone);
    },

    addLeadEmail(leadEmail) {
        updateOrAddObjectInLead('emails', leadEmail);
    },

    updateLeadEmail(leadEmail) {
        updateOrAddObjectInLead('emails', leadEmail);
    },

    deleteLeadEmail(leadEmail) {
        deleteObjectInLead('emails', leadEmail);
    },

    addLeadVehicle(leadVehicle) {
        updateOrAddObjectInLead('vehicles', leadVehicle);
    },

    updateLeadVehicle(leadVehicle) {
        updateOrAddObjectInLead('vehicles', leadVehicle);

        useAssociatedLeadsStore().updateLeadVehicle(leadVehicle);
    },

    deleteLeadVehicle(leadVehicle) {
        deleteObjectInLead('vehicles', leadVehicle);
    },

    addCommunication(communication) {
        updateOrAddObjectInLead('communications', communication);
        useClientCardStore().addCommunication(communication);
    },

    updateCommunication(communication) {
        updateOrAddObjectInLead('communications', communication);
        useClientCardStore().updateCommunication(communication);
    },

    deleteCommunication(communication) {
        deleteObjectInLead('communications', communication);
        useClientCardStore().deleteCommunication(communication);
    },

    addLeadEvent(leadEvent) {
        updateOrAddObjectInLead('lead_events', leadEvent);
    },

    addTaskEvent(taskEvent) {
        updateOrAddObjectInLead('task_events', taskEvent);
        useClientCardStore().addTaskEvent(taskEvent);
    },

    updateTaskEvent(taskEvent) {
        updateOrAddObjectInLead('task_events', taskEvent);
        useClientCardStore().updateTaskEvent(taskEvent);
    },

    deleteTaskEvent(taskEvent) {
        deleteObjectInLead('task_events', taskEvent);
        useClientCardStore().deleteTaskEvent(taskEvent);
    },

    removeLeadFromLocalState(id) {
        const filterLead = (lead) => {
            return lead.id != id;
        };

        useDashboardStore().tableLeads = useDashboardStore().tableLeads.filter(filterLead);
        useDashboardStore().tableLeads.forEach((lead) => {
            if (!Array.isArray(lead.associated_leads)) {
                return;
            }

            lead.associated_leads = lead.associated_leads.filter((leadId) => {
                return leadId != id;
            });
        });

        useAssociatedLeadsStore().removeLead(id);
    },

    deleteLead(id, customerId = getKnownLeadCustomerId(id)) {
        const contextLead = useContextStore().lead;

        if (
            contextLead?.lead_type_id === LeadType.EVENT &&
            (String(contextLead.event_related_id) === String(id) ||
                String(contextLead.id) === String(id))
        ) {
            contextLead.event_parent_available = false;
        }

        this.removeLeadFromLocalState(id);

        invalidateLeadCustomerProfile(customerId);
    },

    updateAutomationJob({ id, leadId, payload }) {
        const updatedAutomationJob = {
            id,
            lead_id: leadId,
            ...payload,
        };

        updateOrAddObjectInLead('automation_jobs', updatedAutomationJob);

        useClientCardStore().updateCommunicationAutomationJob(updatedAutomationJob);
    },

    setMergedLead(mergedLead) {
        this.mergedLead = mergedLead;
    },

    setTaskEventToBeCompleted(taskEvent) {
        this.taskEventToBeCompleted = taskEvent;
    },

    appendNewError(options) {
        const defaults = {
            message: '',
            code: '',
            display: false,
            request: null,
            error: null,
            data: null,
        };

        const { message, code, display, error, ...informations } = {
            ...defaults,
            ...options,
        };
        const responseStatus = error?.response?.status;

        if (![401, 403].includes(responseStatus)) {
            if (!isEmpty(code)) {
                Sentry.withScope((scope) => {
                    scope.setExtras({
                        timestamp: Moment.now().toUtcDateTimeString(),
                        code,
                        error,
                        ...informations,
                    });
                    Sentry.captureMessage(`Error on requests - ${code}`, 'warning');
                });
            } else {
                this.errors.push({
                    timestamp: Moment.now().toUtcDateTimeString(),
                    code,
                    error,
                    ...informations,
                });
            }
        }

        if (display) {
            showError(message, code);
        }
    },

    updateVehicleRecordedDate({ leadId, newLeadData }) {
        if (!('delivered_date' in newLeadData)) {
            return;
        }

        if (useContextStore().leadId != leadId) {
            return;
        }

        const lead = useContextStore().lead;

        if (!lead) {
            return;
        }

        if (!lead.account.auto_recorded_date) {
            return;
        }

        const vehicle = lead.wanted_vehicles.find((v) => v.sold) || lead.wanted_vehicles[0];

        if (vehicle && !vehicle.recorded_date) {
            vehicle.recorded_date = newLeadData.delivered_date;
        }
    },

    setConfigs(configs) {
        this.configs = { ...this.configs, ...configs };
    },

    setFeaturePreviews(featurePreviews) {
        this.featurePreviews = featurePreviews;
    },

    setGroups(groups) {
        this.groups = groups;
    },

    setSpecificPhoneToCall(specificPhoneToCall) {
        this.specificPhoneToCall = specificPhoneToCall;
    },

    updateWebOrderBadgeCount(newCount) {
        this.webOrderBadgeCount = newCount;
    },
    updateSmsBadgeCount(newCount) {
        this.smsBadgeCount = newCount;
    },
    setMergedLeadIdAction(mergedLeadId) {
        if (empty(mergedLeadId)) {
            return;
        }

        // Make sure ID has a valid format
        if (mergedLeadId != parseInt(mergedLeadId, 10)) {
            router.replace('/');
            return;
        }

        api.leads.show(mergedLeadId).then((lead) => {
            this.setMergedLead(lead);
            this.setMergedLeadId(mergedLeadId);
        });
    },
    async reloadLead(leadId) {
        if (!leadId) {
            return;
        }

        const leadExists =
            useContextStore().leadId == leadId ||
            useDashboardStore().tableLeads.some((l) => l.id == leadId);

        if (leadExists) {
            const lead = await api.leads.show(leadId);

            this.updateLead({
                id: lead.id,
                newData: lead,
            });
        }
    },
    leadPhoneCreated(leadPhone) {
        this.addLeadPhone(leadPhone);
    },

    leadPhoneUpdated(leadPhone) {
        this.updateLeadPhone(leadPhone);
    },

    leadPhoneDeleted(leadPhone) {
        this.deleteLeadPhone(leadPhone);
    },

    leadEmailCreated(leadEmail) {
        this.addLeadEmail(leadEmail);
    },

    leadEmailUpdated(leadEmail) {
        this.updateLeadEmail(leadEmail);
    },

    leadEmailDeleted(leadEmail) {
        this.deleteLeadEmail(leadEmail);
    },

    leadVehicleCreated(leadVehicle) {
        this.addLeadVehicle(leadVehicle);
    },

    leadVehicleUpdated(leadVehicle) {
        this.updateLeadVehicle(leadVehicle);
    },

    leadVehicleDeleted(leadVehicle) {
        this.deleteLeadVehicle(leadVehicle);
    },
    communicationCreated(communication) {
        const isMassMailing = communication.kind == CommunicationKind.MASS_SEND;

        if (!isMassMailing) {
            this.addCommunication(communication);
        }
    },
    communicationUpdated(communication) {
        this.updateCommunication(communication);
    },

    communicationDeleted(communication) {
        this.deleteCommunication(communication);
    },

    leadEventCreated(leadEvent) {
        this.addLeadEvent(leadEvent);
    },

    taskEventCreated(taskEvent) {
        this.addTaskEvent(taskEvent);
        eventBus.$emit('task-event-created', taskEvent);
    },
    taskEventUpdated(taskEvent) {
        this.updateTaskEvent(taskEvent);
        eventBus.$emit('task-event-updated', taskEvent);
    },

    taskEventDeleted(taskEvent) {
        this.deleteTaskEvent(taskEvent);
        eventBus.$emit('task-event-deleted', taskEvent);
    },

    commentCreated(comment) {
        if (comment.lead_id == useContextStore().leadId) {
            eventBus.$emit('refresh-comments');
        }
    },

    commentUpdated(comment) {
        if (comment.lead_id == useContextStore().leadId) {
            eventBus.$emit('refresh-comments');
        }
    },

    commentDeleted(comment) {
        if (comment.lead_id == useContextStore().leadId) {
            eventBus.$emit('refresh-comments');
        }
    },
    userSeenEmailRead(communicationId) {
        return new Promise((resolve, reject) => {
            if (!communicationId) {
                return;
            }

            const payload = {
                user_seen_email_read_at: Moment.now().toString(),
                from: 'userSeenEmailRead',
            };

            // Update the user seen email at field
            axios
                .patch(`v1/communications/${communicationId}`, payload)
                .then((response) => {
                    this.communicationUpdated(response.data.data);
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response) {
                        switch (error.response.status) {
                            case 400:
                                showWarning();
                                return;

                            case 403:
                                return;

                            case 404:
                                showWarning(i18n.t('error.leadNotFound'));
                                return;
                        }

                        this.appendNewError({
                            code: '0106',
                            display: false,
                            error,
                            payload: { communicationId, ...payload },
                        });
                    }

                    reject(error);
                });
        });
    },

    clearSingleSeenEmailReadAlert(communication) {
        if (useUserStore().parentAuthUser.isSameUser(communication.user_id)) {
            this.userSeenEmailRead(communication.id);
        }
    },
    clearAllSeenEmailReadAlert(communications) {
        communications.forEach((communication) => {
            if (
                communication.communication_method_id == 2 &&
                communication.communication_type_id == 1 &&
                Moment.asLocale(communication.email_read_at, 'email_read_at').isValid() &&
                !Moment.asLocale(
                    communication.user_seen_email_read_at,
                    'user_seen_email_read_at'
                ).isValid() &&
                useUserStore().parentAuthUser.isSameUser(communication.user_id)
            ) {
                this.userSeenEmailRead(communication.id);
            }
        });
    },
    /**
     * Triggers the request to update a lead.
     */
    async requestUpdateLead(payload, id, options = {}) {
        // Make sure id is set
        if (empty(id)) {
            return null;
        }

        try {
            const traceOptions = assignmentTraceOptions(payload, options.assignmentTrace?.source);
            const response = traceOptions
                ? await axios.put(`v1/leads/${id}`, payload, traceOptions)
                : await axios.put(`v1/leads/${id}`, payload);
            const lead = response.data.data;

            eventBus.$emit('cancel-follow-up');

            // Manually reload calendar since lead is not updated...
            if (payload.main_event_data && payload.main_event_data.canceled) {
                eventBus.$emit('reload-calendar', {
                    leadId: id,
                    force: true,
                });
            }

            const dataToSync = {
                user: lead.user,
                bdc_user: lead.bdc_user,
                commercial: lead.commercial,
                customer: lead.customer,
                service_advisor_user: lead.service_advisor_user,
                service_agent_user: lead.service_agent_user,
                second_user: lead.second_user,
                second_bdc_user: lead.second_bdc_user,
            };

            // Manually update task event in lead...
            if (lead.task_events) {
                dataToSync.task_events = lead.task_events;
            }

            // Manually update wanted_vehicles in lead...
            if (lead.wanted_vehicles) {
                dataToSync.wanted_vehicles = lead.wanted_vehicles;
            }

            this.updateLead({
                id,
                newData: dataToSync,
            });

            invalidateLeadCustomerProfile(lead.customer_id ?? lead.customer?.id);

            return response;
        } catch (error) {
            if (!error.response) {
                return null;
            }

            switch (error.response.status) {
                case 403:
                    return null;

                case 404:
                    showWarning(i18n.t('error.leadNotFound'));
                    return null;
            }

            this.appendNewError({
                code: '0048',
                display: true,
                error,
                payload,
            });
        }

        return null;
    },
    /**
     * Updates a lead. If no leadId is passed, it uses the contextLeadId. (Also updates the stat lead).
     */
    // eslint-disable-next-line consistent-return
    updateLeadAction(leadId, newData, options = {}) {
        eventBus.$emit('fetch-stats', { background: true });
        applyLeadTypeDefaultsOnContextLead(leadId, newData);

        const leadWritebackData = { ...newData };
        const shouldWritebackOptimistically = options.optimisticWriteback !== false;

        if (shouldWritebackOptimistically) {
            this.updateLead({
                id: leadId,
                newData,
            });

            this.updateVehicleRecordedDate({
                leadId,
                newLeadData: newData,
            });
        }

        if (options.customField) {
            options.customField.value = Array.isArray(options.customField.value)
                ? options.customField.value
                : [options.customField.value];

            this.updateLeadCustomable({
                id: leadId,
                newData: options.customField,
            });

            newData = {
                customField: options.customField,
            };
        }

        if (options.mainEvent) {
            newData.main_event_data = options.mainEvent;
        }

        if (options.deleteUpcomingTask) {
            newData.deleteUpcomingTask = options.deleteUpcomingTask;
        }

        const request = options.assignmentTrace
            ? this.requestUpdateLead(newData, leadId, { assignmentTrace: options.assignmentTrace })
            : this.requestUpdateLead(newData, leadId);
        return request.then((response) => {
            if (response && !shouldWritebackOptimistically) {
                const confirmedLead = Object.fromEntries(
                    Object.keys(leadWritebackData).map((key) => [
                        key,
                        Object.hasOwn(response.data.data, key)
                            ? response.data.data[key]
                            : leadWritebackData[key],
                    ])
                );

                this.updateLead({
                    id: leadId,
                    newData: confirmedLead,
                });

                this.updateVehicleRecordedDate({
                    leadId,
                    newLeadData: confirmedLead,
                });
            }

            if (response && options.successCallback) {
                options.successCallback(response.data.data);
            }

            return response;
        });
    },
    /**
     *   Updates multiples leads. If no leadId is passed, no lead will be updated
     */
    async bulkUpdateLeads(newData, leadIds, isBulkAll, dashboardCriteria) {
        if (empty(leadIds) || empty(newData)) {
            return null;
        }

        for (const leadId of leadIds) {
            if (empty(leadId)) {
                continue;
            }

            if (newData.customField) {
                this.updateLeadCustomable({
                    id: leadId,
                    newData: newData.customField,
                });
            } else {
                this.updateLead({
                    id: leadId,
                    newData,
                });
            }
        }

        const query = (() => {
            if (isBulkAll) {
                return { search: dashboardCriteria };
            }
            return { ids: leadIds };
        })();

        try {
            return await api.leads.update(query, {
                ...newData,
                blackListedLeadIds: useDashboardStore().blackListedLeadIds,
            });
        } catch (error) {
            if (error.response?.status === 403) {
                return null;
            }

            this.appendNewError({
                code: '0049',
                display: false,
                error,
                payload: { newData },
            });
        }

        return null;
    },
    /**
     * Updates a product. If no leadId is passed, it uses the contextLeadId.
     */
    async updateLeadProductAction(newData, options = {}) {
        const shouldWritebackOptimistically = options.optimisticWriteback !== false;

        if (shouldWritebackOptimistically) {
            this.updateLeadProduct(newData);
        }

        const leadProduct = {
            id: newData.id,
            sold: newData.pivot.sold,
            price: newData.pivot.price,
            minutes: newData.pivot.minutes,
            notes: newData.pivot.notes,
        };

        const response = await this.requestUpdateLead({ leadProduct }, newData.lead_id);

        if (response && !shouldWritebackOptimistically) {
            this.updateLeadProduct(newData);
        }

        return response;
    },
    /**
     * Adds a vehicle to the lead passed. If no leadId is passed, it uses the contextLeadId.
     */
    addLeadVehicleAction(data, leadId) {
        return new Promise((resolve, reject) => {
            if (empty(data)) {
                resolve(null);
                return;
            }

            // Get lead id from context.lead if not passed
            if (empty(leadId)) {
                if (empty(useContextStore().leadId)) {
                    resolve(null);
                    return;
                }

                leadId = useContextStore().leadId;
            }

            if (empty(leadId)) {
                resolve(null);
                return;
            }

            // Set lead id in params
            data.lead_id = leadId;

            axios
                .post('v1/lead-vehicles', data)
                .then((response) => {
                    this.leadVehicleCreated(response.data.data);
                    invalidateLeadVehicleCustomerProfile(getKnownLeadCustomerId(leadId));
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response && error.response.status === 403) {
                        resolve(null);
                        return;
                    }

                    this.appendNewError({
                        code: '0050',
                        display: true,
                        error,
                        payload: { data },
                    });

                    reject(error);
                });
        });
    },
    /**
     * Deletes a vehicle to from lead passed. If no leadId is passed, it uses the contextLeadId.
     *
     * @param {number | null | undefined} vehicleId
     * @param {number | null | undefined} [leadId]
     * @returns {Promise<import('axios').AxiosResponse | null>}
     */
    deleteLeadVehicleAction(vehicleId, leadId) {
        return new Promise((resolve, reject) => {
            if (empty(vehicleId)) {
                resolve(null);
                return;
            }

            // Get lead id from context.lead if not passed
            if (empty(leadId)) {
                if (empty(useContextStore().leadId)) {
                    resolve(null);
                    return;
                }

                leadId = useContextStore().leadId;
            }

            if (empty(leadId)) {
                resolve(null);
                return;
            }

            axios
                .delete(`v1/lead-vehicles/${vehicleId}`)
                .then((response) => {
                    this.leadVehicleDeleted({ id: vehicleId, lead_id: leadId });
                    invalidateLeadVehicleCustomerProfile(getKnownLeadCustomerId(leadId));
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response && error.response.status === 403) {
                        resolve(null);
                        return;
                    }

                    this.appendNewError({
                        code: '0051',
                        display: false,
                        error,
                        payload: { vehicleId },
                    });

                    reject(error);
                });
        });
    },

    /**
     * Updates a vehicle. If no leadId is passed, it uses the contextLeadId.
     *
     * @param {Record<string, unknown>} payload
     * @param {number | null | undefined} vehicleId
     * @param {number | null | undefined} [leadId]
     * @param {{ customField?: { value: unknown } }} [options]
     * @returns {Promise<import('axios').AxiosResponse | null>}
     */
    async updateLeadVehicleAction(payload, vehicleId, leadId, options) {
        if (empty(vehicleId)) {
            return null;
        }

        // Get lead id from context.lead if not passed
        if (empty(leadId)) {
            if (empty(useContextStore().leadId)) {
                return null;
            }

            leadId = useContextStore().leadId;
        }

        this.leadVehicleUpdated({ ...payload, id: vehicleId, lead_id: leadId });

        if (options && options.customField) {
            options.customField.value = Array.isArray(options.customField.value)
                ? options.customField.value
                : [options.customField.value];

            payload = {
                customField: options.customField,
            };

            this.updateLeadCustomable({
                id: leadId,
                newData: {
                    ...payload.customField,
                    vehicleId,
                },
            });
        }

        try {
            const response = await axios.patch(`v1/lead-vehicles/${vehicleId}`, payload);

            // @TODO Is this required? It's duplicated...
            this.leadVehicleUpdated({
                ...response.data.data,
                id: vehicleId,
                lead_id: leadId,
            });
            invalidateLeadVehicleCustomerProfile(getKnownLeadCustomerId(leadId));

            return response;
        } catch (error) {
            if (error.response && error.response.status === 403) {
                return null;
            }

            if (error.response?.status !== 422) {
                this.appendNewError({
                    code: '0052',
                    display: true,
                    error,
                    payload,
                    vehicleId,
                });
            }

            throw error;
        }
    },

    async bulkUpdateWantedVehicles(payload, leadIds, isBulkAll, dashboardCriteria) {
        useDashboardStore()
            .tableLeads.filter((lead) => {
                return leadIds.includes(lead.id) && !!lead.wanted_vehicles.length;
            })
            .forEach((lead) => {
                const vehicleId = lead.wanted_vehicles[0].id;
                this.leadVehicleUpdated({
                    ...payload,
                    id: vehicleId,
                    lead_id: lead.id,
                });
            });

        const query = (() => {
            if (isBulkAll) {
                return { search: dashboardCriteria };
            }
            return { ids: leadIds };
        })();

        try {
            return await api.leads.update(query, {
                wanted_vehicle: payload,
                blackListedLeadIds: useDashboardStore().blackListedLeadIds,
            });
        } catch (error) {
            if (error.response?.status === 403) {
                return null;
            }

            this.appendNewError({
                code: '0049',
                display: false,
                error,
                payload: { newData },
            });
        }

        return null;
    },

    async bulkUpdateExchangeVehicles(payload, leadIds, isBulkAll, dashboardCriteria) {
        useDashboardStore()
            .tableLeads.filter((lead) => {
                return leadIds.includes(lead.id) && !!lead.exchange_vehicles.length;
            })
            .forEach((lead) => {
                const vehicleId = lead.exchange_vehicles[0].id;
                this.leadVehicleUpdated({
                    ...payload,
                    id: vehicleId,
                    lead_id: lead.id,
                });
            });

        const query = (() => {
            if (isBulkAll) {
                return { search: dashboardCriteria };
            }
            return { ids: leadIds };
        })();

        try {
            return await api.leads.update(query, {
                exchange_vehicle: payload,
                blackListedLeadIds: useDashboardStore().blackListedLeadIds,
            });
        } catch (error) {
            if (error.response?.status === 403) {
                return null;
            }

            this.appendNewError({
                code: '0049',
                display: false,
                error,
                payload: { newData },
            });
        }

        return null;
    },

    /**
     * Updates a vehicle with accidented part. If no leadId is passed, it uses the contextLeadId.
     */
    async updateLeadVehicleAccidentedParts(payload, vehicleId, leadId) {
        if (empty(vehicleId)) {
            return null;
        }

        // Get lead id from context.lead if not passed
        if (empty(leadId)) {
            if (empty(useContextStore().leadId)) {
                return null;
            }

            leadId = useContextStore().leadId;
        }

        this.leadVehicleUpdated({ ...payload, id: vehicleId, lead_id: leadId });

        try {
            const response = await axios.patch(`v1/lead-vehicles/accidented/${vehicleId}`, payload);

            return response.data;
        } catch (error) {
            if (error.response && error.response.status === 403) {
                return null;
            }

            this.appendNewError({
                code: '0060',
                display: false,
                error,
                payload,
                vehicleId,
            });
        }

        return null;
    },

    /**
     * Decodes the vehicle's VIN.
     */
    async decodeVehicleVin({ vin, vehicleId }) {
        const response = await axios.post(`v1/lead-vehicles/${vehicleId}/decode-vin`, {
            vin,
        });

        this.updateLeadVehicle(response.data.data.updatedVehicle);

        return response.data.data;
    },

    /**
     * Decodes the vehicle's VIN.
     */
    async decodeVehicleVinAsl({ vin, vehicleId }) {
        const response = await axios.post(`v1/lead-vehicles/${vehicleId}/asl-decode-vin`, {
            vin,
        });

        this.updateLeadVehicle(response.data.updatedVehicle);

        return {
            ...response.data.data,
            updatedVehicle: response.data.updatedVehicle,
        };
    },

    /**
     * Updates the vehicle's VIN.
     */
    async updateVinVehicleValue({ uvc, vehicleId }) {
        const response = await axios.patch(`v1/lead-vehicles/${vehicleId}/vin-value`, {
            uvc,
        });

        this.updateLeadVehicle(response.data.data.updatedVehicle);

        return response.data.data;
    },

    /**
     * Adds a phone to the lead passed. If no leadId is passed, it uses the contextLeadId.
     */
    addLeadPhoneAction(data, leadId) {
        return new Promise((resolve, reject) => {
            if (empty(data)) {
                return;
            }

            // Get lead id from context.lead if not passed
            if (empty(leadId)) {
                if (empty(useContextStore().leadId)) {
                    return;
                }

                leadId = useContextStore().leadId;
            }

            if (empty(leadId)) {
                return;
            }

            // Set lead id in params
            data.lead_id = leadId;

            axios
                .post('v1/lead-phones', data)
                .then((response) => {
                    this.leadPhoneCreated(response.data.data);
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response && error.response.status === 403) {
                        return;
                    }

                    this.appendNewError({
                        code: '0053',
                        display: false,
                        error,
                        payload: { data },
                    });

                    reject(error);
                });
        });
    },

    /**
     * Deletes a phone to from lead passed. If no leadId is passed, it uses the contextLeadId.
     */
    deleteLeadPhoneAction(phoneId, leadId) {
        return new Promise((resolve, reject) => {
            if (empty(phoneId)) {
                return;
            }

            // Get lead id from context.lead if not passed
            if (empty(leadId)) {
                if (empty(useContextStore().leadId)) {
                    return;
                }

                leadId = useContextStore().leadId;
            }

            if (empty(leadId)) {
                return;
            }

            axios
                .delete(`v1/lead-phones/${phoneId}`)
                .then((response) => {
                    this.leadPhoneDeleted({ id: phoneId, lead_id: leadId });
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response) {
                        switch (error.response.status) {
                            case 400:
                                reject(error);
                                return;

                            case 403:
                                return;
                        }
                    }

                    this.appendNewError({
                        code: '0054',
                        display: false,
                        error,
                        payload: { phoneId },
                    });

                    reject(error);
                });
        });
    },

    /**
     * Updates a phone. If no leadId is passed, it uses the contextLeadId.
     */
    updateLeadPhoneAction(newData, phoneId, leadId) {
        return new Promise((resolve, reject) => {
            if (empty(phoneId)) {
                return;
            }

            // Get lead id from context.lead if not passed
            if (empty(leadId)) {
                if (empty(useContextStore().leadId)) {
                    return;
                }

                leadId = useContextStore().leadId;
            }

            axios
                .patch(`v1/lead-phones/${phoneId}`, newData)
                .then((response) => {
                    this.leadPhoneUpdated(response.data.data);
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response) {
                        switch (error.response.status) {
                            case 400:
                                reject(error);
                                return;

                            case 403:
                                return;
                        }
                    }

                    this.appendNewError({
                        code: '0055',
                        display: false,
                        error,
                        payload: { newData },
                        phoneId,
                    });

                    reject(error);
                });
        });
    },

    /**
     * Adds an email to the lead passed. If no leadId is passed, it uses the contextLeadId.
     * Adds an email to the lead passed. If no leadId is passed, it uses the contextLeadId.
     */
    addLeadEmailAction(data, leadId) {
        return new Promise((resolve, reject) => {
            if (empty(data)) {
                return;
            }

            // Get lead id from context.lead if not passed
            if (empty(leadId)) {
                if (empty(useContextStore().leadId)) {
                    return;
                }

                leadId = useContextStore().leadId;
            }

            if (empty(leadId)) {
                return;
            }

            // Set lead id in params
            data.lead_id = leadId;

            axios
                .post('v1/lead-emails', data)
                .then((response) => {
                    this.leadEmailCreated(response.data.data);
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response && error.response.status === 403) {
                        return;
                    }

                    this.appendNewError({
                        code: '0056',
                        display: false,
                        error,
                        payload: { data },
                    });

                    reject(error);
                });
        });
    },

    /**
     * Deletes an email to from lead passed. If no leadId is passed, it uses the contextLeadId.
     */
    deleteLeadEmailAction(emailId, leadId) {
        return new Promise((resolve, reject) => {
            if (empty(emailId)) {
                return;
            }

            // Get lead id from context.lead if not passed
            if (empty(leadId)) {
                if (empty(useContextStore().leadId)) {
                    return;
                }

                leadId = useContextStore().leadId;
            }

            if (empty(leadId)) {
                return;
            }

            axios
                .delete(`v1/lead-emails/${emailId}`)
                .then((response) => {
                    this.leadEmailDeleted({ id: emailId, lead_id: leadId });
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response && error.response.status === 403) {
                        return;
                    }

                    this.appendNewError({
                        code: '0057',
                        display: false,
                        error,
                        payload: { emailId },
                    });

                    reject(error);
                });
        });
    },

    /**
     * Updates an email. If no leadId is passed, it uses the contextLeadId.
     */
    updateLeadEmailAction(newData, emailId, leadId) {
        return new Promise((resolve, reject) => {
            if (empty(emailId)) {
                return;
            }

            // Get lead id from context.lead if not passed
            if (empty(leadId)) {
                if (empty(useContextStore().leadId)) {
                    return;
                }

                leadId = useContextStore().leadId;
            }

            axios
                .patch(`v1/lead-emails/${emailId}`, newData)
                .then((response) => {
                    this.leadEmailUpdated(response.data.data);
                    resolve(response);
                })
                .catch((error) => {
                    if (error.response && error.response.status === 403) {
                        return;
                    }

                    this.appendNewError({
                        code: '0058',
                        display: false,
                        error,
                        payload: { newData },
                        emailId,
                    });

                    reject(error);
                });
        });
    },

    setUsers(accountId) {
        axios
            .get('v1/users', {
                params: {
                    accountId,
                    with: {
                        account: [
                            'id',
                            'bdc_advisor',
                            'sale_date_month',
                            'leadxpress',
                            'name',
                            'logo_translations',
                            'result_date_validation',
                            'sale_validation',
                            'webboost',
                            'commercial',
                            'client_number',
                            'power_sport',
                            'month_start_day',
                            'next_step_stopping',
                            'sale_table',
                            'sale_accessories',
                            'trade_report',
                            'unsubscribe',
                            'csi',
                            'sale_by_phone',
                            'updated_at',
                        ],
                        objectives: [
                            'id',
                            'sale_amount',
                            'average_value',
                            'defined_date',
                            'user_id',
                            'division_id',
                        ],
                        divisions: ['id', 'name'],
                    },
                    filter: [
                        'id',
                        'first_name',
                        'last_name',
                        'role_id',
                        'post_id',
                        'account_id',
                        'profile_picture',
                        'updated_at',
                    ],
                },
            })
            .then((response) => {
                if (response.data.success) {
                    // Remove commercial users
                    const filteredUsers = response.data.data.filter((value) => {
                        return value.role_id != Role.COMMERCIAL;
                    });

                    useDashboardStore().users = filteredUsers;
                }
            })
            .catch((error) => {
                if (error.response && error.response.status === 403) {
                    return;
                }

                this.appendNewError({
                    code: '0059',
                    display: false,
                    error,
                    payload: { getParams },
                });
            });
    },

    async mergeLeads(from, to, action) {
        try {
            return await api.leads.merge(
                { id: from.id },
                {
                    lang: i18n.getContentLanguage(),
                    oldLeadId: to.id,
                    rule: action,
                }
            );
        } catch (error) {
            if (error.response && error.response.status === 403) {
                return null;
            }

            this.appendNewError({
                code: '0062',
                display: false,
                error,
                payload: {
                    lang: i18n.getContentLanguage(),
                    oldLeadId: to.id,
                    rule: action,
                },
            });

            throw error;
        }
    },

    async fetchVehicleMakes() {
        const response = await axios.get('v1/vehicles/makes');

        return response.data.data;
    },

    async fetchAslMakes() {
        const response = await axios.get('v1/asl/makes');

        return response.data.data;
    },

    async fetchVehicleModels(makeId) {
        const response = await axios.get('v1/vehicles/models', {
            params: {
                make_id: makeId,
            },
        });

        return response.data.data;
    },

    async fetchAslModels({ makeId, year }) {
        const response = await axios.get(`v1/asl/models/${makeId}/${year}`);

        return response.data.data;
    },

    async fetchVehicleYears(modelId) {
        const response = await axios.get('v1/vehicles/years', {
            params: {
                model_id: modelId,
            },
        });

        return response.data.data;
    },

    async fetchVehicleTrims(modelId) {
        const response = await axios.get('v1/vehicles/trims', {
            params: {
                model_id: modelId,
            },
        });

        return response.data.data;
    },

    async fetchAslSpecifications(aslModelId) {
        const response = await axios.get('v1/asl/specifications', {
            params: {
                asl_model_id: aslModelId,
            },
        });

        return response.data.data;
    },

    async fetchVehicleMakeWarranty(make, year) {
        const response = await axios.get('v1/vehicles/make-warranty', {
            params: {
                make,
                year,
            },
        });

        return response.data.data;
    },

    async fetchConfigs() {
        const response = await axios.get('v1/configs');

        this.setConfigs(response.data.data);
    },

    async fetchFeaturePreviews() {
        const response = await axios.get('v1/feature-previews');

        this.setFeaturePreviews(response.data.data);
    },

    async updateAutomationJobAction(params) {
        const { id, payload } = params;

        this.updateAutomationJob(params);

        try {
            await api.automationJobs.update(id, payload);
        } catch (error) {
            notify.error(i18n.t('automations.alerts.update.error'));
        }
    },
    async fetchLead(leadId) {
        const { default: Lead } = await import('@/entities/Lead.js');
        assertEntityImport(Lead, 'Lead');
        return new Lead(await api.leads.show(leadId));
    },
    leadIsInDashboard(leadId) {
        return useDashboardStore().tableLeads.some((lead) => lead.id == leadId);
    },

    getAssignmentUsers() {
        const contextLead = useContextStore().lead;
        const contextAccount = useAccountStore().contextAccount;
        const assignmentUsers = contextAccount.activeUsers;

        const missingUserIds = new Set();

        if (contextLead.userId) {
            const assignedAdvisor = assignmentUsers.some((u) => u.id == contextLead.userId);

            if (!assignedAdvisor) {
                missingUserIds.add(contextLead.userId);
            }
        }

        if (contextAccount.secondary_advisor && contextLead.secondaryUserId) {
            const assignedSecondaryAdvisor = assignmentUsers.some(
                (u) => u.id == contextLead.secondaryUserId
            );

            if (!assignedSecondaryAdvisor) {
                missingUserIds.add(contextLead.secondaryUserId);
            }
        }

        if (contextLead.commercialId) {
            const assignedCommercial = assignmentUsers.some(
                (u) => u.id == contextLead.commercialId
            );

            if (!assignedCommercial) {
                missingUserIds.add(contextLead.commercialId);
            }
        }

        if (contextLead.bdcUserId) {
            const assignedBdc = assignmentUsers.some((u) => u.id == contextLead.bdcUserId);

            if (!assignedBdc) {
                missingUserIds.add(contextLead.bdcUserId);
            }
        }

        if (contextLead.serviceAdvisorId) {
            const assignedServiceAdvisorIncluded = assignmentUsers.some(
                (u) => u.id == contextLead.serviceAdvisorId
            );

            if (!assignedServiceAdvisorIncluded) {
                missingUserIds.add(contextLead.serviceAdvisorId);
            }
        }

        if (contextLead.serviceAgentId) {
            const assignedServiceAgentIncluded = assignmentUsers.some(
                (u) => u.id == contextLead.serviceAgentId
            );

            if (!assignedServiceAgentIncluded) {
                missingUserIds.add(contextLead.serviceAgentId);
            }
        }

        assignmentUsers.unshift(
            ...[...missingUserIds].map((userId) => useUserStore().getUserById(userId))
        );

        return assignmentUsers;
    },
};

const updateOrAddObjectInLead = (key, updatedObject) => {
    if (!updatedObject.id || !updatedObject.lead_id) {
        return;
    }

    const updateObject = (lead) => {
        let existingObject;

        try {
            existingObject = lead[key].find((object) => object.id == updatedObject.id);
        } catch (error) {
            Sentry.withScope((scope) => {
                scope.setExtras({
                    timestamp: Moment.now().toUtcDateTimeString(),
                    lead,
                    key,
                    updatedObject,
                });
                Sentry.captureMessage(
                    'Error on requests - Trying to update undefined lead key in try catch',
                    'warning'
                );
            });

            return;
        }

        if (existingObject) {
            merge(existingObject, updatedObject);
        } else {
            lead[key].push(updatedObject);
        }
    };

    if (useContextStore().leadId == updatedObject.lead_id) {
        updateObject(useContextStore().lead);
    }
    useDashboardStore()
        .tableLeads.filter((lead) => lead.id == updatedObject.lead_id)
        .forEach(updateObject);

    if (['phones', 'emails'].includes(key) && updatedObject.lead_id == useContextStore().leadId) {
        useContextStore().checkCommunicating(updatedObject.lead_id);
    }
};

const deleteObjectInLead = (key, deletedObject) => {
    if (!deletedObject.id || !deletedObject.lead_id) {
        return;
    }

    const deleteObject = (lead) => {
        const index = lead[key].findIndex((object) => object.id == deletedObject.id);

        if (index !== -1) {
            lead[key].splice(index, 1);
        }
    };

    if (useContextStore().leadId == deletedObject.lead_id) {
        deleteObject(useContextStore().lead);
    }
    useDashboardStore()
        .tableLeads.filter((lead) => lead.id == deletedObject.lead_id)
        .forEach(deleteObject);

    if (['phones', 'emails'].includes(key) && deletedObject.lead_id == useContextStore().leadId) {
        useContextStore().checkCommunicating(deletedObject.lead_id);
    }
};
